// Minimal markdown → HTML renderer scoped to the subset used by docs/wiki/.
// No external dep — we control the input and trust it (it's our own checked-in
// markdown), so we don't need full CommonMark + sanitization.
//
// Supports: headings (#–####), paragraphs, bullet/numbered lists (single
// level), tables, fenced code blocks, blockquotes, hr (---), and inline
// bold/italic/code/links. Links pointing at `<slug>.md` are rewritten to
// internal `/wiki/<slug>` routes so the wiki sidebar works.

export function renderMarkdown(md) {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      i++;
      const code = [];
      while (i < lines.length && !lines[i].startsWith('```')) {
        code.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // skip closing fence
      const langClass = lang ? ` class="lang-${escAttr(lang)}"` : '';
      out.push(`<pre><code${langClass}>${escHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    // Horizontal rule
    if (/^\s*---\s*$/.test(line)) {
      out.push('<hr />');
      i++;
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const text = renderInline(h[2]);
      const slug = slugify(h[2]);
      out.push(`<h${level} id="${slug}">${text}</h${level}>`);
      i++;
      continue;
    }

    // Table — header row + separator
    if (
      line.startsWith('|') &&
      i + 1 < lines.length &&
      /^\|[\s|:-]+\|\s*$/.test(lines[i + 1])
    ) {
      const head = parseRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(parseRow(lines[i]));
        i++;
      }
      out.push(buildTable(head, rows));
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s+/.test(line) && !/^\s*---\s*$/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      out.push(`<ul>${items.map(it => `<li>${renderInline(it)}</li>`).join('')}</ul>`);
      continue;
    }

    // Numbered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      out.push(`<ol>${items.map(it => `<li>${renderInline(it)}</li>`).join('')}</ol>`);
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quote.push(lines[i].slice(2));
        i++;
      }
      out.push(`<blockquote>${renderInline(quote.join(' '))}</blockquote>`);
      continue;
    }

    // Blank line → paragraph break (handled by paragraph collector below)
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph — gather until blank or block element
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('```') &&
      !/^#{1,6}\s+/.test(lines[i]) &&
      !/^\s*---\s*$/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !lines[i].startsWith('> ') &&
      !lines[i].startsWith('|')
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length) out.push(`<p>${renderInline(para.join(' '))}</p>`);
  }

  return out.join('\n');
}

// ───── inline ─────

function renderInline(s) {
  // Escape first, then do inline replacements on the escaped text.
  let out = escHtml(s);
  // Inline code (must come before bold/italic so its contents aren't re-processed).
  out = out.replace(/`([^`]+)`/g, (_, c) => `<code>${c}</code>`);
  // Bold (**…**)
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic (*…*) — but not inside `<strong>` we just emitted, since those
  // have no surviving `*`.
  out = out.replace(/(^|[^*])\*([^*\n]+)\*([^*]|$)/g, '$1<em>$2</em>$3');
  // Links [text](url) — rewrites *.md hrefs to /wiki/<slug> for in-app nav.
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => {
    const isMd = /\.md(?:#.*)?$/.test(url);
    if (isMd) {
      const [path, hash = ''] = url.split('#');
      const slug = path.replace(/\.md$/, '');
      return `<a href="/wiki/${slug}${hash ? '#' + hash : ''}">${text}</a>`;
    }
    return `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`;
  });
  return out;
}

// ───── helpers ─────

function escHtml(s) {
  return String(s).replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
}

function escAttr(s) {
  return String(s).replace(/"/g, '&quot;');
}

function parseRow(line) {
  return line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(s => s.trim());
}

function buildTable(head, rows) {
  const ths = head.map(h => `<th>${renderInline(h)}</th>`).join('');
  const trs = rows
    .map(r => `<tr>${r.map(c => `<td>${renderInline(c)}</td>`).join('')}</tr>`)
    .join('');
  return `<table class="wiki-table"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
}

function slugify(s) {
  return s.toLowerCase().trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}
